// frontend/src/components/Layout/Sidebar.tsx
import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Collapse,
  Tooltip,
  Fade,
  IconButton,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  User as UserIcon, 
  Receipt as ReceiptIcon, 
  Boxes, 
  BarChart3, 
  Plus, 
  ShoppingCart as CartIcon, 
  ShoppingBag, 
  BookOpen, 
  Truck, 
  Ship, 
  FileText,
  Tags,
  Package,
  Settings as SettingsIcon,
  ShieldCheck
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useSidebarScrollbar } from '../../hooks/useSidebarScrollbar';
import logo from '../../assets/Sijil.jpg';

export const drawerWidth = 280;
export const collapsedDrawerWidth = 70;

// Menu items configuration — ordered by usage frequency within each placement group.
// placement: 'top' (before Sales/Statement), 'mid' (after Statement, before Purchases), 'bottom' (after logistics)
type MenuPlacement = 'top' | 'mid' | 'bottom';

const menuItems: {
  text: string;
  icon: React.ReactElement;
  path: string;
  roles: string[];
  placement: MenuPlacement;
}[] = [
  { text: 'Organizations', icon: <ShieldCheck size={20} />, path: '/platform/organizations', roles: ['superadmin'], placement: 'top' },
  { text: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/dashboard', roles: ['admin'], placement: 'top' },
  { text: 'New Sale', icon: <Plus size={20} />, path: '/sales/new', roles: ['employee'], placement: 'top' },
  { text: 'Daily Ledger', icon: <BookOpen size={20} />, path: '/daily-ledger', roles: ['admin'], placement: 'mid' },
  { text: 'Customers', icon: <Users size={20} />, path: '/customers', roles: ['admin'], placement: 'mid' },
  { text: 'Suppliers', icon: <Building2 size={20} />, path: '/suppliers', roles: ['admin'], placement: 'mid' },
  { text: 'Products', icon: <Package size={20} />, path: '/products', roles: ['admin'], placement: 'mid' },
  { text: 'Categories', icon: <Tags size={20} />, path: '/categories', roles: ['admin'], placement: 'mid' },
  { text: 'Users', icon: <UserIcon size={20} />, path: '/users', roles: ['admin'], placement: 'bottom' },
  { text: 'Organization Settings', icon: <SettingsIcon size={20} />, path: '/organization/settings', roles: ['admin'], placement: 'bottom' },
];

const salesItems = [
  { text: 'All Sales', path: '/sales', icon: <ReceiptIcon size={18} /> },
  { text: 'New Sale', path: '/sales/new', icon: <Plus size={18} /> },
  { text: 'Sales Report', path: '/sales-report', icon: <BarChart3 size={18} /> },
  { text: 'Customer Outstanding', path: '/customer-outstanding', icon: <ReceiptIcon size={18} />, badge: 5 },
];

const statementItems = [
  { text: 'Search Statement', path: '/statement', icon: <FileText size={18} /> },
  { text: 'Manual Statement', path: '/statement/manual', icon: <Plus size={18} /> },
];

const purchaseItems = [
  { text: 'All Purchases', path: '/purchases', icon: <Boxes size={18} /> },
  { text: 'New Purchase', path: '/purchases/new', icon: <Plus size={18} /> },
  { text: 'Purchase Report', path: '/purchase-report', icon: <BarChart3 size={18} /> },
];

const freightItems = [
  { text: 'All Freight Invoices', path: '/freight-invoices', icon: <Ship size={18} /> },
  { text: 'New Freight Invoice', path: '/freight-invoices/new', icon: <Plus size={18} /> },
];

const transportItems = [
  { text: 'All Transport Invoices', path: '/transport-invoices', icon: <Truck size={18} /> },
  { text: 'New Transport Invoice', path: '/transport-invoices/new', icon: <Plus size={18} /> },
];

const dubaiTransportItems = [
  { text: 'All Dubai Transport Invoices', path: '/dubai-transport-invoices', icon: <Truck size={18} /> },
  { text: 'New Dubai Transport Invoice', path: '/dubai-transport-invoices/new', icon: <Plus size={18} /> },
];

const dubaiClearanceItems = [
  { text: 'All Dubai Clearance Invoices', path: '/dubai-clearance-invoices', icon: <Ship size={18} /> },
  { text: 'New Dubai Clearance Invoice', path: '/dubai-clearance-invoices/new', icon: <Plus size={18} /> },
];

interface SidebarProps {
  mobileOpen: boolean;
  onMobileToggle: () => void;
  onProfileMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  mobileOpen, 
  onMobileToggle, 
  onProfileMenuOpen 
}) => {
  const { sidebarCollapsed } = useSidebar();
  const { user } = useAuth();
  const { mode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use the sidebar scrollbar hook to manage scrollbar visibility
  useSidebarScrollbar();

  // State for collapsible sections
  const [openSales, setOpenSales] = useState(location.pathname.startsWith('/sales'));
  const [openPurchases, setOpenPurchases] = useState(location.pathname.startsWith('/purchases'));
  const [openFreight, setOpenFreight] = useState(location.pathname.startsWith('/freight-invoices'));
  const [openTransport, setOpenTransport] = useState(location.pathname.startsWith('/transport-invoices'));
  const [openDubaiTransport, setOpenDubaiTransport] = useState(location.pathname.startsWith('/dubai-transport-invoices'));
  const [openDubaiClearance, setOpenDubaiClearance] = useState(location.pathname.startsWith('/dubai-clearance-invoices'));
  const [openStatement, setOpenStatement] = useState(location.pathname.startsWith('/statement'));

  // State for popup menus when sidebar is collapsed
  const [salesPopupAnchor, setSalesPopupAnchor] = useState<null | HTMLElement>(null);
  const [purchasesPopupAnchor, setPurchasesPopupAnchor] = useState<null | HTMLElement>(null);
  const [freightPopupAnchor, setFreightPopupAnchor] = useState<null | HTMLElement>(null);
  const [transportPopupAnchor, setTransportPopupAnchor] = useState<null | HTMLElement>(null);
  const [dubaiTransportPopupAnchor, setDubaiTransportPopupAnchor] = useState<null | HTMLElement>(null);
  const [dubaiClearancePopupAnchor, setDubaiClearancePopupAnchor] = useState<null | HTMLElement>(null);
  const [statementPopupAnchor, setStatementPopupAnchor] = useState<null | HTMLElement>(null);

  // Helper functions
  const isSelected = (path: string) => location.pathname === path;

  // Popup handlers for collapsed sidebar
  const handleSalesPopupOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (sidebarCollapsed) {
      setSalesPopupAnchor(event.currentTarget);
    } else {
      setOpenSales(!openSales);
    }
  };

  const handleSalesPopupClose = () => setSalesPopupAnchor(null);

  const handlePurchasesPopupOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (sidebarCollapsed) {
      setPurchasesPopupAnchor(event.currentTarget);
    } else {
      setOpenPurchases(!openPurchases);
    }
  };

  const handlePurchasesPopupClose = () => setPurchasesPopupAnchor(null);

  const handleFreightPopupOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (sidebarCollapsed) {
      setFreightPopupAnchor(event.currentTarget);
    } else {
      setOpenFreight(!openFreight);
    }
  };

  const handleFreightPopupClose = () => setFreightPopupAnchor(null);

  const handleTransportPopupOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (sidebarCollapsed) {
      setTransportPopupAnchor(event.currentTarget);
    } else {
      setOpenTransport(!openTransport);
    }
  };

  const handleTransportPopupClose = () => setTransportPopupAnchor(null);

  const handleDubaiTransportPopupOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (sidebarCollapsed) {
      setDubaiTransportPopupAnchor(event.currentTarget);
    } else {
      setOpenDubaiTransport(!openDubaiTransport);
    }
  };

  const handleDubaiTransportPopupClose = () => setDubaiTransportPopupAnchor(null);

  const handleDubaiClearancePopupOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (sidebarCollapsed) {
      setDubaiClearancePopupAnchor(event.currentTarget);
    } else {
      setOpenDubaiClearance(!openDubaiClearance);
    }
  };

  const handleDubaiClearancePopupClose = () => setDubaiClearancePopupAnchor(null);

  const handleStatementPopupOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (sidebarCollapsed) {
      setStatementPopupAnchor(event.currentTarget);
    } else {
      setOpenStatement(!openStatement);
    }
  };

  const handleStatementPopupClose = () => setStatementPopupAnchor(null);

  const renderFlatMenuItems = (placement: MenuPlacement) =>
    menuItems
      .filter((item) => item.placement === placement && item.roles.includes(user?.role as any))
      .map((item) => {
        const selected = isSelected(item.path);
        return (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <Tooltip
              title={sidebarCollapsed ? item.text : ''}
              placement="right"
              arrow
            >
              <Box>
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    navigate(item.path);
                    onMobileToggle();
                  }}
                  sx={{
                    borderRadius: 2,
                    mx: 0.5,
                    py: 2,
                    px: sidebarCollapsed ? 1 : 3,
                    minHeight: 44,
                    transition: 'all 0.2s ease',
                    background: selected
                      ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)')
                      : 'transparent',
                    color: selected
                      ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                      : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                    fontWeight: selected ? 600 : 500,
                    border: '1px solid transparent',
                    '&:hover': {
                      background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    color: 'inherit',
                    minWidth: sidebarCollapsed ? 32 : 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    '& .MuiSvgIcon-root, & svg': { fontSize: 20 }
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={150}>
                      <ListItemText
                        primary={item.text}
                        sx={{
                          '.MuiTypography-root': {
                            fontWeight: selected ? 600 : 500,
                            fontSize: 14
                          }
                        }}
                      />
                    </Fade>
                  )}
                </ListItemButton>
              </Box>
            </Tooltip>
          </ListItem>
        );
      });

  // Sidebar drawer content
  const drawer = (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      background: mode === 'dark' 
        ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)'
        : 'linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(241, 245, 249, 0.98) 100%)',
      backdropFilter: 'blur(20px)',
      borderRight: mode === 'dark' 
        ? '1px solid rgba(255, 255, 255, 0.1)' 
        : '1px solid rgba(0, 0, 0, 0.1)',
      position: 'relative',
    }}>
      {/* Glass overlay effect */}
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0,
        right: 0, 
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.05)',
        pointerEvents: 'none',
      }} />

      {/* Sidebar Header */}
      <Box sx={{ 
        p: 1, 
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        borderBottom: mode === 'dark' 
          ? '1px solid rgba(255, 255, 255, 0.1)' 
          : '1px solid rgba(0, 0, 0, 0.1)',
      }}>
        <Box sx={{ 
          position: 'absolute', 
          top: -50, 
          right: -50, 
          width: 100, 
          height: 100, 
          background: mode === 'dark' 
            ? 'rgba(168, 85, 247, 0.15)' 
            : 'rgba(139, 92, 246, 0.1)', 
          borderRadius: '50%', 
          filter: 'blur(20px)',
        }} />
        <Box sx={{ 
          position: 'absolute', 
          bottom: -30, 
          left: -30, 
          width: 60, 
          height: 60, 
          background: mode === 'dark' 
            ? 'rgba(59, 130, 246, 0.15)' 
            : 'rgba(99, 102, 241, 0.1)', 
          borderRadius: '50%', 
          filter: 'blur(15px)',
        }} />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ 
            width: 48, 
            height: 48, 
            borderRadius: 3, 
            overflow: 'hidden',
            boxShadow: mode === 'dark' 
              ? '0 8px 32px rgba(0,0,0,0.3)' 
              : '0 8px 32px rgba(0,0,0,0.1)',
            border: mode === 'dark' 
              ? '2px solid rgba(168, 85, 247, 0.3)' 
              : '2px solid rgba(139, 92, 246, 0.2)',
            background: mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
          }}>
            <img 
              src={logo} 
              alt="Sijil Logo" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover' 
              }} 
            />
          </Box>
          {!sidebarCollapsed && (
            <Fade in={!sidebarCollapsed} timeout={300}>
              <Box>
                <Typography variant="h5" sx={{ 
                  fontWeight: 800, 
                  letterSpacing: 0.5,
                  color: mode === 'dark' ? '#ffffff' : '#1e293b',
                  textShadow: mode === 'dark' 
                    ? '0 2px 4px rgba(0,0,0,0.3)' 
                    : '0 1px 2px rgba(0,0,0,0.1)',
                }}>
                  Sijil
                </Typography>
                <Typography variant="caption" sx={{ 
                  opacity: 0.9, 
                  letterSpacing: 0.5,
                  color: mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.8)' 
                    : 'rgba(30, 41, 59, 0.7)',
                }}>
                  Accounting System
                </Typography>
              </Box>
            </Fade>
          )}
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ 
        flex: 1, 
        p: 2, 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 1,
      }}>
        {!sidebarCollapsed && (
          <Fade in={!sidebarCollapsed} timeout={300}>
            <Typography variant="overline" sx={{ 
              px: 2, 
              py: 1, 
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.8)', 
              fontWeight: 600, 
              letterSpacing: 1, 
              flexShrink: 0,
              textShadow: mode === 'dark' ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
            }}>
              Navigation
            </Typography>
          </Fade>
        )}
        
        <List sx={{ 
          mt: 1, 
          flex: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(168, 85, 247, 0.3)',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(168, 85, 247, 0.5)',
          },
        }}>
          {/* Top menu items (Dashboard / Organizations / employee New Sale) */}
          {renderFlatMenuItems('top')}

          {user?.role === 'admin' && (<>
          {/* Sales Section */}
          {!sidebarCollapsed && (
            <Fade in={!sidebarCollapsed} timeout={300}>
              <Typography variant="overline" sx={{ 
                px: 3, 
                py: 0.5, 
                mt: 1,
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.8)', 
                fontWeight: 600, 
                letterSpacing: 1,
                textShadow: mode === 'dark' ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
              }}>
                Sales
              </Typography>
            </Fade>
          )}
          
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <Tooltip 
              title={sidebarCollapsed ? 'Sales' : ''} 
              placement="right"
              arrow
            >
              <Box>
                <ListItemButton
                  onClick={handleSalesPopupOpen}
                  sx={{
                    borderRadius: 2,
                    mx: 0.5,
                    py: 2,
                    px: sidebarCollapsed ? 1 : 3,
                    minHeight: 44,
                    transition: 'all 0.2s ease',
                    background: openSales 
                      ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') 
                      : 'transparent',
                    color: openSales 
                      ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                      : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                    fontWeight: openSales ? 600 : 500,
                    border: '1px solid transparent',
                    '&:hover': {
                      background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: 'inherit', 
                    minWidth: sidebarCollapsed ? 32 : 40,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                    '& .MuiSvgIcon-root, & svg': { fontSize: 20 }
                  }}>
                    <CartIcon size={20} />
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      <ListItemText primary="Sales" />
                    </Fade>
                  )}
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      {openSales ? <ExpandLess /> : <ExpandMore />}
                    </Fade>
                  )}
                </ListItemButton>
              </Box>
            </Tooltip>
          </ListItem>
          
          {/* Sales Popup Menu for Collapsed Sidebar */}
          <Menu
            anchorEl={salesPopupAnchor}
            open={Boolean(salesPopupAnchor)}
            onClose={handleSalesPopupClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                ml: 1,
                minWidth: 200,
                background: mode === 'dark' 
                  ? 'rgba(15, 23, 42, 0.98)' 
                  : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: mode === 'dark' 
                  ? '1px solid rgba(255, 255, 255, 0.15)' 
                  : '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                borderRadius: 2,
                '& .MuiMenuItem-root': {
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  color: mode === 'dark' 
                    ? 'rgba(241, 245, 249, 0.9)' 
                    : 'rgba(30, 41, 59, 0.9)',
                  '&:hover': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.6)' 
                      : 'rgba(229, 231, 235, 0.6)',
                    color: mode === 'dark' ? '#ffffff' : '#1e293b',
                  },
                  '&.Mui-selected': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.9)' 
                      : 'rgba(99, 102, 241, 0.2)',
                    color: mode === 'dark' ? '#ffffff' : '#1f2937',
                  },
                },
              },
            }}
          >
            {salesItems.map((item) => {
              const selected = isSelected(item.path);
              return (
                <MenuItem
                  key={item.text}
                  selected={selected}
                  onClick={() => {
                    navigate(item.path);
                    handleSalesPopupClose();
                    onMobileToggle();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Box sx={{ 
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
                      {item.text}
                    </Typography>
                  </Box>
                  {item.badge && (
                    <Badge 
                      badgeContent={item.badge} 
                      color="error" 
                      sx={{ 
                        '& .MuiBadge-badge': {
                          fontSize: '0.7rem',
                          height: 18,
                          minWidth: 18,
                        }
                      }}
                    />
                  )}
                </MenuItem>
              );
            })}
          </Menu>
          
          <Collapse in={openSales && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ 
              '& .MuiListItem-root': { 
                padding: 0,
                margin: '1px 0'
              },
              '& .MuiListItemButton-root': {
                paddingLeft: 6,
                paddingY: 1.5,
                marginX: 0.5,
                borderRadius: 2,
                background: 'transparent',
                border: '1px solid transparent',
                '&:hover': {
                  background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                }
              }
            }}>
              {salesItems.map((sub) => {
                const selected = isSelected(sub.path);
                return (
                  <ListItem key={sub.text} disablePadding>
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        navigate(sub.path);
                        onMobileToggle();
                      }}
                      sx={{ 
                        pl: 6, 
                        py: 1.5, 
                        mx: 0.5, 
                        borderRadius: 2,
                        color: selected 
                          ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                          : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                        background: selected ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') : 'transparent',
                        border: '1px solid transparent',
                        '&:hover': {
                          background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: 'inherit', 
                        minWidth: 32,
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                      }}>
                        {sub.icon}
                      </ListItemIcon>
                      <ListItemText primary={sub.text} />
                      {sub.badge && (
                        <Badge 
                          badgeContent={sub.badge} 
                          color="error" 
                          sx={{ 
                            '& .MuiBadge-badge': {
                              fontSize: '0.7rem',
                              height: 18,
                              minWidth: 18,
                            }
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>

          {/* Statement Section */}
          {!sidebarCollapsed && (
            <Fade in={!sidebarCollapsed} timeout={300}>
              <Typography variant="overline" sx={{ 
                px: 3, 
                py: 0.5, 
                mt: 1,
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.8)', 
                fontWeight: 600, 
                letterSpacing: 1,
                textShadow: mode === 'dark' ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
              }}>
                Statement
              </Typography>
            </Fade>
          )}
          
          <ListItem disablePadding sx={{ mb: 0.5, mt: 0.5 }}>
            <Tooltip 
              title={sidebarCollapsed ? 'Statement' : ''} 
              placement="right"
              arrow
            >
              <Box>
                <ListItemButton
                  onClick={handleStatementPopupOpen}
                  sx={{
                    borderRadius: 2,
                    mx: 0.5,
                    py: 2,
                    px: sidebarCollapsed ? 1 : 3,
                    minHeight: 44,
                    transition: 'all 0.2s ease',
                    background: openStatement 
                      ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') 
                      : 'transparent',
                    color: openStatement 
                      ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                      : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                    fontWeight: openStatement ? 600 : 500,
                    border: '1px solid transparent',
                    '&:hover': {
                      background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: 'inherit', 
                    minWidth: sidebarCollapsed ? 32 : 40,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                    '& .MuiSvgIcon-root, & svg': { fontSize: 20 }
                  }}>
                    <FileText size={20} />
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      <ListItemText primary="Statement" />
                    </Fade>
                  )}
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      {openStatement ? <ExpandLess /> : <ExpandMore />}
                    </Fade>
                  )}
                </ListItemButton>
              </Box>
            </Tooltip>
          </ListItem>
          
          {/* Statement Popup Menu for Collapsed Sidebar */}
          <Menu
            anchorEl={statementPopupAnchor}
            open={Boolean(statementPopupAnchor)}
            onClose={handleStatementPopupClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                ml: 1,
                minWidth: 200,
                background: mode === 'dark' 
                  ? 'rgba(15, 23, 42, 0.98)' 
                  : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: mode === 'dark' 
                  ? '1px solid rgba(255, 255, 255, 0.15)' 
                  : '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                borderRadius: 2,
                '& .MuiMenuItem-root': {
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  color: mode === 'dark' 
                    ? 'rgba(241, 245, 249, 0.9)' 
                    : 'rgba(30, 41, 59, 0.9)',
                  '&:hover': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.6)' 
                      : 'rgba(229, 231, 235, 0.6)',
                    color: mode === 'dark' ? '#ffffff' : '#1e293b',
                  },
                  '&.Mui-selected': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.9)' 
                      : 'rgba(99, 102, 241, 0.2)',
                    color: mode === 'dark' ? '#ffffff' : '#1f2937',
                  },
                },
              },
            }}
          >
            {statementItems.map((item) => {
              const selected = isSelected(item.path);
              return (
                <MenuItem
                  key={item.text}
                  selected={selected}
                  onClick={() => {
                    navigate(item.path);
                    handleStatementPopupClose();
                    onMobileToggle();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Box sx={{ 
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
                      {item.text}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Menu>
          
          <Collapse in={openStatement && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ 
              '& .MuiListItem-root': { 
                padding: 0,
                margin: '1px 0'
              },
              '& .MuiListItemButton-root': {
                paddingLeft: 6,
                paddingY: 1.5,
                marginX: 0.5,
                borderRadius: 2,
                background: 'transparent',
                border: '1px solid transparent',
                '&:hover': {
                  background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                }
              }
            }}>
              {statementItems.map((sub) => {
                const selected = isSelected(sub.path);
                return (
                  <ListItem key={sub.text} disablePadding>
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        navigate(sub.path);
                        onMobileToggle();
                      }}
                      sx={{ 
                        pl: 6, 
                        py: 1.5, 
                        mx: 0.5, 
                        borderRadius: 2,
                        color: selected 
                          ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                          : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                        background: selected ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') : 'transparent',
                        border: '1px solid transparent',
                        '&:hover': {
                          background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: 'inherit', 
                        minWidth: 32,
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                      }}>
                        {sub.icon}
                      </ListItemIcon>
                      <ListItemText primary={sub.text} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>

          {/* Mid-frequency items */}
          {renderFlatMenuItems('mid')}

          {/* Purchases Section */}
          {!sidebarCollapsed && (
            <Fade in={!sidebarCollapsed} timeout={300}>
              <Typography variant="overline" sx={{ 
                px: 3, 
                py: 0.5, 
                mt: 1,
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.8)', 
                fontWeight: 600, 
                letterSpacing: 1,
                textShadow: mode === 'dark' ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
              }}>
                Purchases
              </Typography>
            </Fade>
          )}
          
          <ListItem disablePadding sx={{ mb: 0.5, mt: 0.5 }}>
            <Tooltip 
              title={sidebarCollapsed ? 'Purchases' : ''} 
              placement="right"
              arrow
            >
              <Box>
                <ListItemButton
                  onClick={handlePurchasesPopupOpen}
                  sx={{
                    borderRadius: 2,
                    mx: 0.5,
                    py: 2,
                    px: sidebarCollapsed ? 1 : 3,
                    minHeight: 44,
                    transition: 'all 0.2s ease',
                    background: openPurchases 
                      ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') 
                      : 'transparent',
                    color: openPurchases 
                      ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                      : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                    fontWeight: openPurchases ? 600 : 500,
                    border: '1px solid transparent',
                    '&:hover': {
                      background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: 'inherit', 
                    minWidth: sidebarCollapsed ? 32 : 40,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                    '& .MuiSvgIcon-root, & svg': { fontSize: 20 }
                  }}>
                    <ShoppingBag size={20} />
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      <ListItemText primary="Purchases" />
                    </Fade>
                  )}
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      {openPurchases ? <ExpandLess /> : <ExpandMore />}
                    </Fade>
                  )}
                </ListItemButton>
              </Box>
            </Tooltip>
          </ListItem>
          
          {/* Purchases Popup Menu for Collapsed Sidebar */}
          <Menu
            anchorEl={purchasesPopupAnchor}
            open={Boolean(purchasesPopupAnchor)}
            onClose={handlePurchasesPopupClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                ml: 1,
                minWidth: 200,
                background: mode === 'dark' 
                  ? 'rgba(15, 23, 42, 0.98)' 
                  : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: mode === 'dark' 
                  ? '1px solid rgba(255, 255, 255, 0.15)' 
                  : '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                borderRadius: 2,
                '& .MuiMenuItem-root': {
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  color: mode === 'dark' 
                    ? 'rgba(241, 245, 249, 0.9)' 
                    : 'rgba(30, 41, 59, 0.9)',
                  '&:hover': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.6)' 
                      : 'rgba(229, 231, 235, 0.6)',
                    color: mode === 'dark' ? '#ffffff' : '#1e293b',
                  },
                  '&.Mui-selected': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.9)' 
                      : 'rgba(99, 102, 241, 0.2)',
                    color: mode === 'dark' ? '#ffffff' : '#1f2937',
                  },
                },
              },
            }}
          >
            {purchaseItems.map((item) => {
              const selected = isSelected(item.path);
              return (
                <MenuItem
                  key={item.text}
                  selected={selected}
                  onClick={() => {
                    navigate(item.path);
                    handlePurchasesPopupClose();
                    onMobileToggle();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Box sx={{ 
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
                      {item.text}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Menu>
          
          <Collapse in={openPurchases && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ 
              '& .MuiListItem-root': { 
                padding: 0,
                margin: '1px 0'
              },
              '& .MuiListItemButton-root': {
                paddingLeft: 6,
                paddingY: 1.5,
                marginX: 0.5,
                borderRadius: 2,
                background: 'transparent',
                border: '1px solid transparent',
                '&:hover': {
                  background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                }
              }
            }}>
              {purchaseItems.map((sub) => {
                const selected = isSelected(sub.path);
                return (
                  <ListItem key={sub.text} disablePadding>
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        navigate(sub.path);
                        onMobileToggle();
                      }}
                      sx={{ 
                        pl: 6, 
                        py: 1.5, 
                        mx: 0.5, 
                        borderRadius: 2,
                        color: selected 
                          ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                          : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                        background: selected ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') : 'transparent',
                        border: '1px solid transparent',
                        '&:hover': {
                          background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: 'inherit', 
                        minWidth: 32,
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                      }}>
                        {sub.icon}
                      </ListItemIcon>
                      <ListItemText primary={sub.text} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>

          {/* Freight Section */}
          {!sidebarCollapsed && (
            <Fade in={!sidebarCollapsed} timeout={300}>
              <Typography variant="overline" sx={{ 
                px: 3, 
                py: 0.5, 
                mt: 1,
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.8)', 
                fontWeight: 600, 
                letterSpacing: 1,
                textShadow: mode === 'dark' ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
              }}>
                Freight
              </Typography>
            </Fade>
          )}
          
          <ListItem disablePadding sx={{ mb: 0.5, mt: 0.5 }}>
            <Tooltip 
              title={sidebarCollapsed ? 'Freight' : ''} 
              placement="right"
              arrow
            >
              <Box>
                <ListItemButton
                  onClick={handleFreightPopupOpen}
                  sx={{
                    borderRadius: 2,
                    mx: 0.5,
                    py: 2,
                    px: sidebarCollapsed ? 1 : 3,
                    minHeight: 44,
                    transition: 'all 0.2s ease',
                    background: openFreight 
                      ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') 
                      : 'transparent',
                    color: openFreight 
                      ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                      : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                    fontWeight: openFreight ? 600 : 500,
                    border: '1px solid transparent',
                    '&:hover': {
                      background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: 'inherit', 
                    minWidth: sidebarCollapsed ? 32 : 40,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                    '& .MuiSvgIcon-root, & svg': { fontSize: 20 }
                  }}>
                    <Ship size={20} />
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      <ListItemText primary="Freight" />
                    </Fade>
                  )}
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      {openFreight ? <ExpandLess /> : <ExpandMore />}
                    </Fade>
                  )}
                </ListItemButton>
              </Box>
            </Tooltip>
          </ListItem>
          
          {/* Freight Popup Menu for Collapsed Sidebar */}
          <Menu
            anchorEl={freightPopupAnchor}
            open={Boolean(freightPopupAnchor)}
            onClose={handleFreightPopupClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                ml: 1,
                minWidth: 200,
                background: mode === 'dark' 
                  ? 'rgba(15, 23, 42, 0.98)' 
                  : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: mode === 'dark' 
                  ? '1px solid rgba(255, 255, 255, 0.15)' 
                  : '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                borderRadius: 2,
                '& .MuiMenuItem-root': {
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  color: mode === 'dark' 
                    ? 'rgba(241, 245, 249, 0.9)' 
                    : 'rgba(30, 41, 59, 0.9)',
                  '&:hover': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.6)' 
                      : 'rgba(229, 231, 235, 0.6)',
                    color: mode === 'dark' ? '#ffffff' : '#1e293b',
                  },
                  '&.Mui-selected': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.9)' 
                      : 'rgba(99, 102, 241, 0.2)',
                    color: mode === 'dark' ? '#ffffff' : '#1f2937',
                  },
                },
              },
            }}
          >
            {freightItems.map((item) => {
              const selected = isSelected(item.path);
              return (
                <MenuItem
                  key={item.text}
                  selected={selected}
                  onClick={() => {
                    navigate(item.path);
                    handleFreightPopupClose();
                    onMobileToggle();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Box sx={{ 
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
                      {item.text}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Menu>
          
          <Collapse in={openFreight && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ 
              '& .MuiListItem-root': { 
                padding: 0,
                margin: '1px 0'
              },
              '& .MuiListItemButton-root': {
                paddingLeft: 6,
                paddingY: 1.5,
                marginX: 0.5,
                borderRadius: 2,
                background: 'transparent',
                border: '1px solid transparent',
                '&:hover': {
                  background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                }
              }
            }}>
              {freightItems.map((sub) => {
                const selected = isSelected(sub.path);
                return (
                  <ListItem key={sub.text} disablePadding>
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        navigate(sub.path);
                        onMobileToggle();
                      }}
                      sx={{ 
                        pl: 6, 
                        py: 1.5, 
                        mx: 0.5, 
                        borderRadius: 2,
                        color: selected 
                          ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                          : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                        background: selected ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') : 'transparent',
                        border: '1px solid transparent',
                        '&:hover': {
                          background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: 'inherit', 
                        minWidth: 32,
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                      }}>
                        {sub.icon}
                      </ListItemIcon>
                      <ListItemText primary={sub.text} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>

          {/* Transport Section */}
          {!sidebarCollapsed && (
            <Fade in={!sidebarCollapsed} timeout={300}>
              <Typography variant="overline" sx={{ 
                px: 3, 
                py: 0.5, 
                mt: 1,
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.8)', 
                fontWeight: 600, 
                letterSpacing: 1,
                textShadow: mode === 'dark' ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
              }}>
                Transport
              </Typography>
            </Fade>
          )}
          
          <ListItem disablePadding sx={{ mb: 0.5, mt: 0.5 }}>
            <Tooltip 
              title={sidebarCollapsed ? 'Transport' : ''} 
              placement="right"
              arrow
            >
              <Box>
                <ListItemButton
                  onClick={handleTransportPopupOpen}
                  sx={{
                    borderRadius: 2,
                    mx: 0.5,
                    py: 2,
                    px: sidebarCollapsed ? 1 : 3,
                    minHeight: 44,
                    transition: 'all 0.2s ease',
                    background: openTransport 
                      ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') 
                      : 'transparent',
                    color: openTransport 
                      ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                      : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                    fontWeight: openTransport ? 600 : 500,
                    border: '1px solid transparent',
                    '&:hover': {
                      background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: 'inherit', 
                    minWidth: sidebarCollapsed ? 32 : 40,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                    '& .MuiSvgIcon-root, & svg': { fontSize: 20 }
                  }}>
                    <Truck size={20} />
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      <ListItemText primary="Transport" />
                    </Fade>
                  )}
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      {openTransport ? <ExpandLess /> : <ExpandMore />}
                    </Fade>
                  )}
                </ListItemButton>
              </Box>
            </Tooltip>
          </ListItem>
          
          {/* Transport Popup Menu for Collapsed Sidebar */}
          <Menu
            anchorEl={transportPopupAnchor}
            open={Boolean(transportPopupAnchor)}
            onClose={handleTransportPopupClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                ml: 1,
                minWidth: 200,
                background: mode === 'dark' 
                  ? 'rgba(15, 23, 42, 0.98)' 
                  : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: mode === 'dark' 
                  ? '1px solid rgba(255, 255, 255, 0.15)' 
                  : '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                borderRadius: 2,
                '& .MuiMenuItem-root': {
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  color: mode === 'dark' 
                    ? 'rgba(241, 245, 249, 0.9)' 
                    : 'rgba(30, 41, 59, 0.9)',
                  '&:hover': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.6)' 
                      : 'rgba(229, 231, 235, 0.6)',
                    color: mode === 'dark' ? '#ffffff' : '#1e293b',
                  },
                  '&.Mui-selected': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.9)' 
                      : 'rgba(99, 102, 241, 0.2)',
                    color: mode === 'dark' ? '#ffffff' : '#1f2937',
                  },
                },
              },
            }}
          >
            {transportItems.map((item) => {
              const selected = isSelected(item.path);
              return (
                <MenuItem
                  key={item.text}
                  selected={selected}
                  onClick={() => {
                    navigate(item.path);
                    handleTransportPopupClose();
                    onMobileToggle();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Box sx={{ 
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
                      {item.text}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Menu>
          
          <Collapse in={openTransport && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ 
              '& .MuiListItem-root': { 
                padding: 0,
                margin: '1px 0'
              },
              '& .MuiListItemButton-root': {
                paddingLeft: 6,
                paddingY: 1.5,
                marginX: 0.5,
                borderRadius: 2,
                background: 'transparent',
                border: '1px solid transparent',
                '&:hover': {
                  background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                }
              }
            }}>
              {transportItems.map((sub) => {
                const selected = isSelected(sub.path);
                return (
                  <ListItem key={sub.text} disablePadding>
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        navigate(sub.path);
                        onMobileToggle();
                      }}
                      sx={{ 
                        pl: 6, 
                        py: 1.5, 
                        mx: 0.5, 
                        borderRadius: 2,
                        color: selected 
                          ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                          : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                        background: selected ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') : 'transparent',
                        border: '1px solid transparent',
                        '&:hover': {
                          background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: 'inherit', 
                        minWidth: 32,
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                      }}>
                        {sub.icon}
                      </ListItemIcon>
                      <ListItemText primary={sub.text} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>

          {/* Dubai Transport Section */}
          {!sidebarCollapsed && (
            <Fade in={!sidebarCollapsed} timeout={300}>
              <Typography variant="overline" sx={{ 
                px: 3, 
                py: 0.5, 
                mt: 1,
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.8)', 
                fontWeight: 600, 
                letterSpacing: 1,
                textShadow: mode === 'dark' ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
              }}>
                Dubai Transport
              </Typography>
            </Fade>
          )}
          
          <ListItem disablePadding sx={{ mb: 0.5, mt: 0.5 }}>
            <Tooltip 
              title={sidebarCollapsed ? 'Dubai Transport' : ''} 
              placement="right"
              arrow
            >
              <Box>
                <ListItemButton
                  onClick={handleDubaiTransportPopupOpen}
                  sx={{
                    borderRadius: 2,
                    mx: 0.5,
                    py: 2,
                    px: sidebarCollapsed ? 1 : 3,
                    minHeight: 44,
                    transition: 'all 0.2s ease',
                    background: openDubaiTransport 
                      ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') 
                      : 'transparent',
                    color: openDubaiTransport 
                      ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                      : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                    fontWeight: openDubaiTransport ? 600 : 500,
                    border: '1px solid transparent',
                    '&:hover': {
                      background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: 'inherit', 
                    minWidth: sidebarCollapsed ? 32 : 40,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                    '& .MuiSvgIcon-root, & svg': { fontSize: 20 }
                  }}>
                    <Truck size={20} />
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      <ListItemText primary="Dubai Transport" />
                    </Fade>
                  )}
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      {openDubaiTransport ? <ExpandLess /> : <ExpandMore />}
                    </Fade>
                  )}
                </ListItemButton>
              </Box>
            </Tooltip>
          </ListItem>
          
          {/* Dubai Transport Popup Menu for Collapsed Sidebar */}
          <Menu
            anchorEl={dubaiTransportPopupAnchor}
            open={Boolean(dubaiTransportPopupAnchor)}
            onClose={handleDubaiTransportPopupClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                ml: 1,
                minWidth: 200,
                background: mode === 'dark' 
                  ? 'rgba(15, 23, 42, 0.98)' 
                  : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: mode === 'dark' 
                  ? '1px solid rgba(255, 255, 255, 0.15)' 
                  : '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                borderRadius: 2,
                '& .MuiMenuItem-root': {
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  color: mode === 'dark' 
                    ? 'rgba(241, 245, 249, 0.9)' 
                    : 'rgba(30, 41, 59, 0.9)',
                  '&:hover': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.6)' 
                      : 'rgba(229, 231, 235, 0.6)',
                    color: mode === 'dark' ? '#ffffff' : '#1e293b',
                  },
                  '&.Mui-selected': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.9)' 
                      : 'rgba(99, 102, 241, 0.2)',
                    color: mode === 'dark' ? '#ffffff' : '#1f2937',
                  },
                },
              },
            }}
          >
            {dubaiTransportItems.map((item) => {
              const selected = isSelected(item.path);
              return (
                <MenuItem
                  key={item.text}
                  selected={selected}
                  onClick={() => {
                    navigate(item.path);
                    handleDubaiTransportPopupClose();
                    onMobileToggle();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Box sx={{ 
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
                      {item.text}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Menu>
          
          <Collapse in={openDubaiTransport && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ 
              '& .MuiListItem-root': { 
                padding: 0,
                margin: '1px 0'
              },
              '& .MuiListItemButton-root': {
                paddingLeft: 6,
                paddingY: 1.5,
                marginX: 0.5,
                borderRadius: 2,
                background: 'transparent',
                border: '1px solid transparent',
                '&:hover': {
                  background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                }
              }
            }}>
              {dubaiTransportItems.map((sub) => {
                const selected = isSelected(sub.path);
                return (
                  <ListItem key={sub.text} disablePadding>
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        navigate(sub.path);
                        onMobileToggle();
                      }}
                      sx={{ 
                        pl: 6, 
                        py: 1.5, 
                        mx: 0.5, 
                        borderRadius: 2,
                        color: selected 
                          ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                          : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                        background: selected ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') : 'transparent',
                        border: '1px solid transparent',
                        '&:hover': {
                          background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: 'inherit', 
                        minWidth: 32,
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                      }}>
                        {sub.icon}
                      </ListItemIcon>
                      <ListItemText primary={sub.text} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>

          {/* Dubai Clearance Section */}
          {!sidebarCollapsed && (
            <Fade in={!sidebarCollapsed} timeout={300}>
              <Typography variant="overline" sx={{ 
                px: 3, 
                py: 0.5, 
                mt: 1,
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.8)', 
                fontWeight: 600, 
                letterSpacing: 1,
                textShadow: mode === 'dark' ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
              }}>
                Dubai Clearance
              </Typography>
            </Fade>
          )}
          
          <ListItem disablePadding sx={{ mb: 0.5, mt: 0.5 }}>
            <Tooltip 
              title={sidebarCollapsed ? 'Dubai Clearance' : ''} 
              placement="right"
              arrow
            >
              <Box>
                <ListItemButton
                  onClick={handleDubaiClearancePopupOpen}
                  sx={{
                    borderRadius: 2,
                    mx: 0.5,
                    py: 2,
                    px: sidebarCollapsed ? 1 : 3,
                    minHeight: 44,
                    transition: 'all 0.2s ease',
                    background: openDubaiClearance 
                      ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') 
                      : 'transparent',
                    color: openDubaiClearance 
                      ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                      : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                    fontWeight: openDubaiClearance ? 600 : 500,
                    border: '1px solid transparent',
                    '&:hover': {
                      background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: 'inherit', 
                    minWidth: sidebarCollapsed ? 32 : 40,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                    '& .MuiSvgIcon-root, & svg': { fontSize: 20 }
                  }}>
                    <Ship size={20} />
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      <ListItemText primary="Dubai Clearance" />
                    </Fade>
                  )}
                  {!sidebarCollapsed && (
                    <Fade in={!sidebarCollapsed} timeout={300}>
                      {openDubaiClearance ? <ExpandLess /> : <ExpandMore />}
                    </Fade>
                  )}
                </ListItemButton>
              </Box>
            </Tooltip>
          </ListItem>
          
          {/* Dubai Clearance Popup Menu for Collapsed Sidebar */}
          <Menu
            anchorEl={dubaiClearancePopupAnchor}
            open={Boolean(dubaiClearancePopupAnchor)}
            onClose={handleDubaiClearancePopupClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                ml: 1,
                minWidth: 200,
                background: mode === 'dark' 
                  ? 'rgba(15, 23, 42, 0.98)' 
                  : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: mode === 'dark' 
                  ? '1px solid rgba(255, 255, 255, 0.15)' 
                  : '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                borderRadius: 2,
                '& .MuiMenuItem-root': {
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  color: mode === 'dark' 
                    ? 'rgba(241, 245, 249, 0.9)' 
                    : 'rgba(30, 41, 59, 0.9)',
                  '&:hover': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.6)' 
                      : 'rgba(229, 231, 235, 0.6)',
                    color: mode === 'dark' ? '#ffffff' : '#1e293b',
                  },
                  '&.Mui-selected': {
                    background: mode === 'dark' 
                      ? 'rgba(31, 41, 55, 0.9)' 
                      : 'rgba(99, 102, 241, 0.2)',
                    color: mode === 'dark' ? '#ffffff' : '#1f2937',
                  },
                },
              },
            }}
          >
            {dubaiClearanceItems.map((item) => {
              const selected = isSelected(item.path);
              return (
                <MenuItem
                  key={item.text}
                  selected={selected}
                  onClick={() => {
                    navigate(item.path);
                    handleDubaiClearancePopupClose();
                    onMobileToggle();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Box sx={{ 
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
                      {item.text}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Menu>
          
          <Collapse in={openDubaiClearance && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ 
              '& .MuiListItem-root': { 
                padding: 0,
                margin: '1px 0'
              },
              '& .MuiListItemButton-root': {
                paddingLeft: 6,
                paddingY: 1.5,
                marginX: 0.5,
                borderRadius: 2,
                background: 'transparent',
                border: '1px solid transparent',
                '&:hover': {
                  background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                }
              }
            }}>
              {dubaiClearanceItems.map((sub) => {
                const selected = isSelected(sub.path);
                return (
                  <ListItem key={sub.text} disablePadding>
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        navigate(sub.path);
                        onMobileToggle();
                      }}
                      sx={{ 
                        pl: 6, 
                        py: 1.5, 
                        mx: 0.5, 
                        borderRadius: 2,
                        color: selected 
                          ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                          : (mode === 'dark' ? 'rgba(241,245,249,0.9)' : '#1f2937'),
                        background: selected ? (mode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(99, 102, 241, 0.2)') : 'transparent',
                        border: '1px solid transparent',
                        '&:hover': {
                          background: mode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(229,231,235,0.6)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: 'inherit', 
                        minWidth: 32,
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                      }}>
                        {sub.icon}
                      </ListItemIcon>
                      <ListItemText primary={sub.text} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>

          {/* Lower-frequency / settings items */}
          {renderFlatMenuItems('bottom')}
          </>)}

        </List>
      </Box>

      {/* User Profile Section */}
      {/* Minimal User Profile Section */}
<Box
  sx={{
    p: 2,
    borderTop: mode === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(0,0,0,0.08)",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "all 0.2s ease",
  }}
>
  {/* Left side: Avatar + Info */}
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
    {/* Avatar with subtle gradient ring and online dot */}
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          p: 0.5,
          borderRadius: "50%",
          background:
            mode === "dark"
              ? "linear-gradient(135deg, rgba(99,102,241,0.5), rgba(168,85,247,0.4))"
              : "linear-gradient(135deg, rgba(59,130,246,0.25), rgba(99,102,241,0.15))",
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            fontSize: 14,
            fontWeight: 700,
            bgcolor: mode === "dark" ? "#111827" : "#ffffff",
            color: mode === "dark" ? "#e5e7eb" : "#1f2937",
            boxShadow: mode === "dark" ? "0 0 0 2px #0b1220" : "0 0 0 2px #fff",
          }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 2,
          bottom: 2,
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: "#22c55e",
          boxShadow: "0 0 0 2px rgba(0,0,0,0.6)",
        }}
      />
    </Box>

    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: 13,
            color: mode === "dark" ? "#f8fafc" : "#0f172a",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {user?.name || "User"}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontSize: 11,
          color: mode === "dark" ? "#94a3b8" : "#6b7280",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={user?.email || "user@example.com"}
      >
        {user?.email || "user@example.com"}
      </Typography>
    </Box>
  </Box>

  {/* Right side: Dropdown / Actions */}
  <IconButton
    size="small"
    onClick={onProfileMenuOpen}
    sx={{
      color: mode === "dark" ? "#9ca3af" : "#6b7280",
      "&:hover": {
        color: mode === "dark" ? "#fff" : "#111827",
      },
    }}
  >
    <ExpandMore fontSize="small" />
  </IconButton>
</Box>

    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ 
        width: { 
          sm: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth 
        }, 
        flexShrink: { sm: 0 },
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'fixed',
        left: 0,
        top: 0,
        height: '100vh',
        zIndex: 1200,
      }}
      aria-label="mailbox folders"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRight: 'none',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
            borderRight: 'none',
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;